import React, { useState } from 'react';
import { isEmpty } from 'lodash-es';
import { Alert } from '@strava/ui/Alert';
import { useStravaTranslations } from '@strava/container-context';
import {
  DisclaimerText,
  AccountAuthButton,
  OrDivider,
  APPLE,
  FACEBOOK,
  GOOGLE,
  EmailRegistrationForm,
  AlertTypes,
  AnalyticsFieldsTypes,
  EmailRegistrationFormProps
} from '@strava/authentication-ui';
import clsx from '@strava/ui/clsx/clsx';
import styles from './DesktopRegistrationOptions.module.scss';

const i18nPrefix = 'account_registration_page_ui';

export type DesktopRegistrationOptionsProps = EmailRegistrationFormProps & {
  cohort?: string;
  flashMessage?: string;
};

const DesktopRegistrationOptions = ({
  emailAuthorizationUrl,
  handleExtAuthBtnClick,
  cohort = 'control',
  flashMessage,
  track,
  ...emailRegFormProps
}: DesktopRegistrationOptionsProps & {
  emailAuthorizationUrl: string;
  track: (fields: AnalyticsFieldsTypes) => void;
  handleExtAuthBtnClick: (
    option: typeof FACEBOOK | typeof GOOGLE | typeof APPLE
  ) => void;
}) => {
  const [alert, setAlert] = useState<AlertTypes>(
    flashMessage ? { severity: 'error', message: flashMessage } : null
  );

  const { t } = useStravaTranslations();

  // https://strava.atlassian.net/wiki/spaces/TG/pages/2175959148/14+Register+free+apple+sign+up+method+split+test+Desktop
  // variant-a adds the apple sign up, variant-b adds the apple sign up AND shows the email form first
  const showEmailFirst = cohort === 'variant-b';

  const externalSignups = (
    <>
      <AccountAuthButton
        variant="facebook"
        onClick={() => handleExtAuthBtnClick(FACEBOOK)}
        data-testid="facebook_auth_btn"
        className={clsx(showEmailFirst && styles.noMarginTop)}
      >
        {t(`${i18nPrefix}.signup_ctas.facebook_cta`)}
      </AccountAuthButton>

      <AccountAuthButton
        variant="google"
        onClick={() => handleExtAuthBtnClick(GOOGLE)}
        data-testid="google_auth_btn"
      >
        {t(`${i18nPrefix}.signup_ctas.google_cta`)}
      </AccountAuthButton>

      {cohort !== 'control' && (
        <AccountAuthButton
          variant="apple"
          onClick={() => handleExtAuthBtnClick(APPLE)}
          data-testid="apple_auth_btn"
        >
          {t(`${i18nPrefix}.signup_ctas.apple_cta`)}
        </AccountAuthButton>
      )}
    </>
  );

  const emailSignup = (
    <>
      <EmailRegistrationForm
        authorizationUrl={emailAuthorizationUrl}
        setAlert={setAlert}
        track={track}
        idPrefix="desktop"
        {...emailRegFormProps}
      />

      <DisclaimerText
        i18nPrefix={i18nPrefix}
        className={clsx(
          styles.disclaimerText,
          cohort === 'variant-b' && styles.variant
        )}
      />
    </>
  );

  return (
    <div className={styles.registrationOptions}>
      {!isEmpty(alert) && (
        <Alert data-cy="alert" alertType={alert.severity}>
          {alert.message}
        </Alert>
      )}

      {showEmailFirst ? emailSignup : externalSignups}

      <OrDivider text={t(`${i18nPrefix}.or`)} />

      {showEmailFirst ? externalSignups : emailSignup}

      <p className={clsx(styles.loginOption, showEmailFirst && styles.variant)}>
        {t(`${i18nPrefix}.login_option.already_a_member`)}{' '}
        <a href="/login" data-cy="already_member_log_in">
          {t(`${i18nPrefix}.login_option.log_in`)}
        </a>
      </p>
    </div>
  );
};

export default DesktopRegistrationOptions;
